<template>
  <teleport to="body">
    <el-drawer v-model="drawer" direction="rtl" size="50%" class="history-tasks-drawer">
      <template #header>
        <h4 class="history-title">历史待办记录</h4>
      </template>
      <template #default>
        <div class="history-todo-filter">
          <el-date-picker v-model="historyFilter.date" type="daterange" range-separator="至" start-placeholder="开始日期"
            end-placeholder="结束日期" :clearable="false" style="width: 280px;" />
          <el-input v-model="historyFilter.message" placeholder="请输入关键字" clearable style="width: 220px;" />
          <el-select v-model="historyFilter.type" clearable placeholder="请选择类型" style="width: 160px;">
            <el-option label="全部" :value="null" />
            <el-option label="日报" :value="1" />
            <el-option label="运维诊断" :value="5" />
            <el-option label="告警" :value="2" />
            <el-option label="通知" :value="0" />
            <!-- <el-option label="提醒" :value="3" />
            <el-option label="申请" :value="4" /> -->
          </el-select>
          <el-select v-model="historyFilter.state" clearable placeholder="请选择状态" style="width: 140px;">
            <el-option label="全部" :value="null" />
            <el-option label="未读" :value="1" />
            <el-option label="已读" :value="2" />
          </el-select>
          <el-button type="primary" class="history-reset-btn" @click="onHistoryReset">重置</el-button>
        </div>
        <el-table :data="historyTodoList" class="history-table" empty-text="暂无数据" v-loading="loading" :max-height="680">
          <el-table-column prop="datetime" label="日期" width="200" />
          <el-table-column prop="type" label="类型" width="80">
            <template #default="scope">
              <span>{{ typeLabel(scope.row.type) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="message" label="内容" min-width="200" />
          <el-table-column prop="isRead" label="状态" width="80">
            <template #default="scope">
              <span :class="{ 'is-read': scope.row.isRead }">{{ scope.row.isRead ? '已读' : '未读' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="100">
            <template #default="scope">
              <el-button
                link
                class="history-view-btn"
                size="small"
                :disabled="isNotificationTodo(scope.row)"
                @click="handleViewTodo(scope.row)"
              >查看</el-button>
            </template>
          </el-table-column>
        </el-table>
        <div class="history-pagination">
          <div class="history-total">总数：{{ historyPage.total }} 条</div>
          <v-pagination v-model="historyPage.page" :length="historyTotalPages" rounded="circle" size="small"
            :total-visible="10" @update:modelValue="handleHistoryPageChange" />
        </div>
      </template>
    </el-drawer>

    <AlarmDialog v-model="alarmDialog" :task="getActiveData(2)" />
    <DailyReport v-model:detailsVisible="dailyReportDialog" :task="getActiveData(1)" />
    <AlarmReportDrawer v-model="alarmReportDialog" :report-id="getActiveData(5).messageId" />
  </teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import dayjs from 'dayjs'
import { getTodoList, switchTodoReadStatus } from '@/api/workbench/index.js'
import AlarmDialog from '../Tasks/AlarmDialog.vue'
import AlarmReportDrawer from '../Tasks/AlarmReportDrawer.vue'
import DailyReport from '../details.vue'

const drawer = defineModel('modelValue', {
  default: false,
  type: Boolean
})

function createDefaultDateRange() {
  return [dayjs().subtract(7, 'day').toDate(), dayjs().toDate()]
}

const historyFilter = ref({
  date: createDefaultDateRange(),
  message: '',
  type: null,
  state: null
})
const historyTodoList = ref([])
const loading = ref(false)

function getDateRangeValue() {
  const dateRange = historyFilter.value.date
  if (Array.isArray(dateRange) && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
    return dateRange
  }
  const fallback = createDefaultDateRange()
  historyFilter.value.date = fallback
  return fallback
}

const fetchHistoryTodoList = async () => {
  const dateRange = getDateRangeValue()
  const params: Record<string, any> = {
    pageNum: historyPage.value.page,
    pageSize: historyPage.value.pageSize,
    startTime: dayjs(dateRange[0]).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
    endTime: dayjs(dateRange[1]).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
    message: historyFilter.value.message,
  }
  if (historyFilter.value.type != null) {
    params.type = historyFilter.value.type
  }
  if (historyFilter.value.state != null) params.state = historyFilter.value.state
  loading.value = true
  try {
    const response = await getTodoList(params)
    historyTodoList.value = response?.data?.list || []
    historyPage.value.total = response?.data?.total || 0
  } finally {
    loading.value = false
  }
}
const historyPage = ref({
  page: 1,
  pageSize: 20,
  total: 0
})

const historyTotalPages = computed(() => {
  return Math.max(1, Math.ceil(historyPage.value.total / historyPage.value.pageSize))
})

const handleHistoryPageChange = async (page) => {
  historyPage.value.page = page
  await fetchHistoryTodoList()
}

function onHistoryReset() {
  historyFilter.value = {
    date: createDefaultDateRange(),
    message: '',
    type: null,
    state: null
  }
  historyPage.value.page = 1
  fetchHistoryTodoList()
}

function typeLabel(type) {
  const map = { 0: '通知', 1: '日报', 2: '告警', 3: '提醒', 4: '申请', 5: '运维诊断' }
  return map[type] || '其他'
}

function isNotificationTodo(row) {
  return Number(row?.type) === 0
}

const alarmDialog = ref(false)
const dailyReportDialog = ref(false)
const alarmReportDialog = ref(false)
const activeData = ref({})
function setActiveData(type, data) {
  activeData.value[type] = data
}
function getActiveData(type) {
  return activeData.value[type] || {}
}
async function markTodoRead(todo) {
  if (todo?.isRead === true) return
  const id = todo?.id ?? todo?.todoId
  if (!id) return
  await switchTodoReadStatus(id, { state: 2 })
  todo.isRead = true
}

const handleViewTodo = async (todo) => {
  if (isNotificationTodo(todo)) return
  await markTodoRead(todo)
  setActiveData(todo.type, todo)
  if (Number(todo.type) === 2) {
    alarmDialog.value = true
  } else if (Number(todo.type) === 1) {
    dailyReportDialog.value = true
  } else if (Number(todo.type) === 5) {
    alarmReportDialog.value = true
  }
}

watch(() => drawer.value, (newVal) => {
  if (newVal) {
    historyPage.value.page = 1
    fetchHistoryTodoList()
  }
})

watch(
  () => [historyFilter.value.date, historyFilter.value.message, historyFilter.value.type, historyFilter.value.state],
  () => {
    if (!drawer.value) return
    historyPage.value.page = 1
    fetchHistoryTodoList()
  },
  { deep: true }
)
</script>

<style scoped lang="scss">
.history-title {
  margin: 0;
  font-size: 18px;
}

.history-todo-filter {
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.history-table {
  width: 100%;

  :deep(.el-table th.el-table__cell),
  :deep(.el-table td.el-table__cell) {
    padding: 0 12px;
    font-size: 14px;
  }

  :deep(.el-table th.el-table__cell) {
    height: 48px;
    line-height: 48px;
  }

  :deep(.el-table td.el-table__cell) {
    height: 52px;
    line-height: 52px;
  }

  :deep(.el-table__header th) {
    background: #F7F8FA;
    color: #86909C;
    font-weight: 500;
  }

  :deep(.el-table__row:nth-child(odd) td.el-table__cell) {
    background: rgba(235, 240, 255, 0.5);
  }

  :deep(.el-table__row:hover > td.el-table__cell) {
    background: #F2F3F5 !important;
  }
}

.history-pagination {
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.history-total {
  font-size: 14px;
  color: #4E5969;
}

.history-reset-btn {
  background: #426CF9;
  border-color: #426CF9;
  color: #fff;
}

.history-reset-btn:hover,
.history-reset-btn:focus {
  background: #5b81ff;
  border-color: #5b81ff;
  color: #fff;
}

.history-view-btn {
  color: #426CF9;
}

.history-view-btn.is-disabled {
  color: #c0c4cc;
  cursor: not-allowed;
}

.is-read {
  color: #00a870;
}
</style>

